import React from 'react';

// DESIGN.md §7 — one SVG set. 20×20 grid, 1.5px stroke, currentColor, round
// caps, no fills, no color of their own. Sizes: 16 inline with `meta`, 20
// default, 24 in display contexts. Icons inherit text color and take semantic
// role colors from §2.2 at the call site, never here.
//
// This replaces 46 distinct glyphs across three incompatible visual registers
// (emoji, geometric shapes, media symbols). Not every glyph gets an icon: §7
// removes the decorative ones outright — 🏈 🏆 🎯 📈 🔎 🔮 🤖 📁 📂 📋 📚 🚧 🌟
// and the 📏 ⚖️ 🎓 📍 bio-line set, which were labelling four self-evident
// values. Those call sites are rebuilt on type and rule instead.
//
// aria-hidden throughout: every icon in this product sits next to a text label
// or inside a button with an accessible name. An icon that ever becomes the
// only content of a control needs a label at that call site, not here.
function Svg({ size = 20, className, children, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      {...rest}
    >
      {children}
    </svg>
  );
}

// ─── Rating ───────────────────────────────────────────────────────────────
// The one icon allowed a fill: a rating star reads as filled or empty, and an
// outline-only star cannot carry that state. Filled takes `grade`, empty
// `carbon-600` (§7).
export const IconStar = ({ filled = false, ...p }) => (
  <Svg {...p}>
    <path
      d="M10 2.5l2.35 4.76 5.25.76-3.8 3.7.9 5.23L10 14.48l-4.7 2.47.9-5.23-3.8-3.7 5.25-.76L10 2.5z"
      fill={filled ? 'currentColor' : 'none'}
    />
  </Svg>
);

// ─── Status ───────────────────────────────────────────────────────────────
export const IconCheck = (p) => (
  <Svg {...p}><path d="M4 10.5l4 4 8-9" /></Svg>
);

export const IconClose = (p) => (
  <Svg {...p}><path d="M5 5l10 10M15 5L5 15" /></Svg>
);

export const IconWarning = (p) => (
  <Svg {...p}>
    <path d="M10 2.8L18.2 17H1.8L10 2.8z" />
    <path d="M10 8v3.6M10 14.2v.1" />
  </Svg>
);

export const IconLock = (p) => (
  <Svg {...p}>
    <rect x="4" y="8.8" width="12" height="8.2" rx="1" />
    <path d="M6.8 8.8V6.4a3.2 3.2 0 016.4 0v2.4" />
  </Svg>
);

export const IconDot = (p) => (
  <Svg {...p}><circle cx="10" cy="10" r="3.2" fill="currentColor" stroke="none" /></Svg>
);

// ─── Trend (§7: caret, live / danger) ─────────────────────────────────────
export const IconCaretUp = (p) => (
  <Svg {...p}><path d="M4.5 12.5L10 7l5.5 5.5" /></Svg>
);

export const IconCaretDown = (p) => (
  <Svg {...p}><path d="M4.5 7.5L10 13l5.5-5.5" /></Svg>
);

// ─── Navigation ───────────────────────────────────────────────────────────
export const IconChevronRight = (p) => (
  <Svg {...p}><path d="M7.5 4.5L13 10l-5.5 5.5" /></Svg>
);

export const IconChevronDown = (p) => (
  <Svg {...p}><path d="M4.5 7.5L10 13l5.5-5.5" /></Svg>
);

export const IconArrowLeft = (p) => (
  <Svg {...p}><path d="M16 10H4M9 5l-5 5 5 5" /></Svg>
);

export const IconArrowRight = (p) => (
  <Svg {...p}><path d="M4 10h12M11 5l5 5-5 5" /></Svg>
);

// ─── Reorder (§7: ▲ ▼ ⏫ ⏬) ───────────────────────────────────────────────
export const IconArrowUp = (p) => (
  <Svg {...p}><path d="M10 16V4M5 9l5-5 5 5" /></Svg>
);

export const IconArrowDown = (p) => (
  <Svg {...p}><path d="M10 4v12M5 11l5 5 5-5" /></Svg>
);

export const IconArrowToTop = (p) => (
  <Svg {...p}><path d="M4 3.5h12M10 17V7M6 11l4-4 4 4" /></Svg>
);

export const IconArrowToBottom = (p) => (
  <Svg {...p}><path d="M4 16.5h12M10 3v10M6 9l4 4 4-4" /></Svg>
);

export const IconSort = (p) => (
  <Svg {...p}><path d="M7 16V4M4 7l3-3 3 3M13 4v12M10 13l3 3 3-3" /></Svg>
);

export const IconGrip = (p) => (
  <Svg {...p} strokeWidth={0}>
    {[6, 10, 14].map(y => (
      <React.Fragment key={y}>
        <circle cx="7.5" cy={y} r="1.15" fill="currentColor" />
        <circle cx="12.5" cy={y} r="1.15" fill="currentColor" />
      </React.Fragment>
    ))}
  </Svg>
);

// ─── Actions ──────────────────────────────────────────────────────────────
export const IconNote = (p) => (
  <Svg {...p}>
    <path d="M4 3.5h12v13H4z" />
    <path d="M6.8 7.5h6.4M6.8 10.5h6.4M6.8 13.5h3.6" />
  </Svg>
);

export const IconEdit = (p) => (
  <Svg {...p}>
    <path d="M13.4 3.6l3 3L7.6 15.4l-3.9.9.9-3.9 8.8-8.8z" />
  </Svg>
);

export const IconDownload = (p) => (
  <Svg {...p}><path d="M10 3v9M6 8.5l4 4 4-4M3.5 16.5h13" /></Svg>
);

export const IconRefresh = (p) => (
  <Svg {...p}>
    <path d="M16.5 8.5A6.5 6.5 0 004.7 5.6M3.5 11.5a6.5 6.5 0 0011.8 2.9" />
    <path d="M16.5 3.8v4.7h-4.7M3.5 16.2v-4.7h4.7" />
  </Svg>
);

export const IconUndo = (p) => (
  <Svg {...p}>
    <path d="M3.5 8.5h9a4 4 0 010 8H8" />
    <path d="M6.8 4.8L3.3 8.5l3.5 3.7" />
  </Svg>
);

// ─── Transport (§7: ⏸ ▶ ⏹ ⏩) ─────────────────────────────────────────────
export const IconPlay = (p) => (
  <Svg {...p}><path d="M6.5 4.2l9 5.8-9 5.8V4.2z" /></Svg>
);

export const IconPause = (p) => (
  <Svg {...p}><path d="M7.3 4.5v11M12.7 4.5v11" /></Svg>
);

export const IconStop = (p) => (
  <Svg {...p}><rect x="5" y="5" width="10" height="10" rx="0.5" /></Svg>
);

export const IconFastForward = (p) => (
  <Svg {...p}><path d="M3 5l6 5-6 5V5zM11 5l6 5-6 5V5z" /></Svg>
);

// ─── Featured (§7: 🔥 removed; featured is a `grade` flag) ────────────────
export const IconFlag = (p) => (
  <Svg {...p}>
    <path d="M5 17V3.5h10l-2.2 3.5L15 10.5H5" fill="currentColor" stroke="currentColor" />
  </Svg>
);
